import { useEffect, useEffectEvent, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@w/ui/components/button'
import { IconButton } from '@w/ui/components/icon-button'
import { MessageSquareIcon, SquareIcon, SendIcon, CopyIcon } from '@w/ui/components/icon'
import type { BlockReviewSelection } from '../../types/review.types'
import type { CoreViewApi } from '../view-api'
import { blockElement, useReview } from './context'
import { NestedCommentComposer } from './composer'
import { COMMENT_LANE_GAP, commentLaneWidth } from './comments'
import { getOwnedReviewSelectionRect, readBlockReviewTarget, readReviewSelection, reviewToolbarPosition, sameReviewSelection, selectedTexts } from './selection'
import { SELECT_CHANGE_EVENT, START_BLOCK_COMMENT_EVENT, START_SELECTION_COMMENT_EVENT, clearReviewChangeHighlights, selectReviewChange, reviewChangeRect } from './change-ranges'
import { MobileReviewPanel, useMobileReview } from './mobile-review-panel'
import { COMMENT_REF_HIGHLIGHT_CLASSES, PENDING_COMMENT_ID_PREFIX } from './views'
import { EDITABLE } from '../dom-selection'

/** The draft is DOM-only; opening/cancelling a comment never writes to the document. */
function markDraft(root: HTMLElement, range: Range) {
  const surface = root.parentElement
  if (surface) surface.dataset.reviewCommentPending = 'true'
  const restore: (() => void)[] = []
  for (const { node, start, end } of selectedTexts(root, range)) {
    const original = node.data
    const tail = end < node.length ? node.splitText(end) : null
    const selected = start > 0 ? node.splitText(start) : node
    const mark = root.ownerDocument.createElement('mark')
    mark.dataset.reviewCommentPending = 'true'
    mark.dataset.commentId = `${PENDING_COMMENT_ID_PREFIX}selection`
    mark.className = `text-inherit ${COMMENT_REF_HIGHLIGHT_CLASSES.join(' ')}`
    mark.style.setProperty('anchor-name', '--content-block-review-comment-draft')
    selected.before(mark)
    mark.append(selected)
    restore.push(() => {
      mark.replaceWith(selected)
      if (selected !== node) selected.remove()
      tail?.remove()
      node.data = original
    })
  }
  return () => {
    if (surface) delete surface.dataset.reviewCommentPending
    for (const cleanup of restore) cleanup()
  }
}

/** An editable document must keep React-owned text nodes intact while the composer is open. */
function highlightEditableDraft(root: HTMLElement, range: Range) {
  const key = `content-block-review-comment-draft-${crypto.randomUUID()}`
  root.dataset.reviewCommentPending = 'true'
  const surface = root.parentElement
  if (surface) surface.dataset.reviewCommentPending = 'true'
  const style = root.ownerDocument.createElement('style')
  style.textContent = `::highlight(${key}) { background-color: var(--color-surface-warning); text-decoration: underline; text-decoration-color: var(--color-warning); text-decoration-thickness: 2px; }`
  root.ownerDocument.head.append(style)
  if (typeof Highlight !== 'undefined') CSS.highlights?.set(key, new Highlight(range))
  return () => {
    delete root.dataset.reviewCommentPending
    if (surface) delete surface.dataset.reviewCommentPending
    CSS.highlights?.delete(key)
    style.remove()
  }
}

export function ReviewSelectionToolbar({ api }: { api: CoreViewApi }) {
  const { review } = useReview()
  const mobile = useMobileReview()
  const { enabled: mobileEnabled, open: openMobilePanel } = mobile
  const [selection, setSelection] = useState<BlockReviewSelection | null>(null)
  const [draft, setDraft] = useState(false)
  const [body, setBody] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'pending' | 'copied' | 'failed'>('idle')
  const copyVersion = useRef(0)
  const restore = useRef<(() => void) | null>(null)
  const nativeRange = useRef<Range | null>(null)
  const toolbar = useRef<HTMLDivElement>(null)
  const latest = useRef(selection)
  latest.current = selection
  const touch = api.layout === 'touch'
  const collapsedActions = touch && !mobileEnabled
  const native = api.root?.ownerDocument.getSelection()
  const selectedLeaves = selection && api.root && native?.rangeCount ? selectedTexts(api.root, native.getRangeAt(0)) : []
  const selectedMarks = selectedLeaves.map(({ node }) => node.parentElement?.closest('[data-core-comment-ref]'))
  const insideComment = selectedMarks.length > 0 && !!selectedMarks[0] && selectedMarks.every(mark => mark === selectedMarks[0])
  const actions = selection ? review?.getActions?.(selection).filter(action => !action.disabled) ?? [] : []
  const canComment = !!selection && !!review?.onAddComment && !insideComment
  const showCommentAction = canComment && (api.mode !== 'edit' || selection?.scope === 'block')
  const canStartSelectionComment = () => {
    if (!selection || !review?.onAddComment || !api.root) return false
    const live = api.root.ownerDocument.getSelection()
    if (!live?.rangeCount || live.isCollapsed) return false
    const leaves = selectedTexts(api.root, live.getRangeAt(0))
    const marks = leaves.map(({ node }) => node.parentElement?.closest('[data-core-comment-ref]'))
    const inComment = marks.length > 0 && !!marks[0] && marks.every(mark => mark === marks[0])
    return !inComment
  }
  function dismiss() {
    clearReviewChangeHighlights(api.root)
    copyVersion.current += 1
    setCopyStatus('idle')
    mobile.close('selection')
    restore.current?.()
    restore.current = null
    nativeRange.current = null
    setDraft(false)
    setBody('')
    setSelection(null)
    setMenuOpen(false)
    review?.onSelectionChange?.(null)
  }
  const commentShortcut = useEffectEvent((event: KeyboardEvent) => {
    if (canComment) { event.preventDefault(); beginComment() }
  })
  const startSelectionComment = useEffectEvent(() => { if (canStartSelectionComment()) beginComment() })
  useEffect(() => {
    const root = api.root
    if (!root) return () => {}
    root.addEventListener(START_SELECTION_COMMENT_EVENT, startSelectionComment)
    return () => { root.removeEventListener(START_SELECTION_COMMENT_EVENT, startSelectionComment) }
  }, [api.root])
  useEffect(() => () => { copyVersion.current += 1; restore.current?.() }, [])
  useEffect(() => {
    if (!selection || !review || draft) return () => {}
    const keydown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || (event.target instanceof Element && event.target.closest(`input, textarea, ${EDITABLE}`))) return
      const action = review.getActions?.(selection).find(value => !value.disabled && value.shortcut?.toLowerCase() === event.key.toLowerCase())
      if (action) { event.preventDefault(); review.onAction?.(action.id, selection) }
      else if (event.key.toLowerCase() === 'c') commentShortcut(event)
    }
    document.addEventListener('keydown', keydown)
    return () => { document.removeEventListener('keydown', keydown) }
  }, [selection, review, draft])
  useEffect(() => {
    const root = api.root
    if (!root || !review || api.mode === 'answer') return () => {}
    let pointer: { x: number; y: number; time: number } | null = null
    let tapped = true
    let pressed = false
    const pointerDown = (event: PointerEvent) => { pointer = { x: event.clientX, y: event.clientY, time: Date.now() }; tapped = true; pressed = true }
    function update(event?: Event) {
      const fromPress = pressed
      if (event?.type === 'click') pressed = false
      if (event?.type === 'pointerup') {
        const ended = event as PointerEvent
        tapped = !pointer || Date.now() - pointer.time <= 400 && Math.hypot(ended.clientX - pointer.x, ended.clientY - pointer.y) <= 8
        pointer = null
      }
      if (draft || !root || !review) return
      if ((event?.type === 'click' || event?.type === 'pointerup') && event.target instanceof Element && event.target.closest('button, a, input, textarea, [data-comment-id]')) return
      let next = readReviewSelection(root, api.state.blocks, review)
      const nativeOwner = next !== null
      const dom = root.ownerDocument.getSelection()
      if (next && dom?.rangeCount && !dom.isCollapsed) nativeRange.current = dom.getRangeAt(0).cloneRange()
      const diff = event?.target instanceof Element ? event.target.closest<HTMLElement>('[data-diff-kind]') : null
      if (event?.type === 'click' && diff && (!fromPress || tapped && dom?.isCollapsed !== false)) {
        const id = diff.dataset.blockId
        if (id && diff.dataset.diffKind !== 'context') selectReviewChange(root, id)
        return
      }
      if (!next && !tapped && (event?.type === 'pointerup' || event?.type === 'click')) return
      if (!next && api.mode === 'review' && (event?.type === 'pointerup' || event?.type === 'click') && api.layout === 'touch' && tapped) {
        const element = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-review-id]') ?? event.target.closest<HTMLElement>('[data-block-id]') : null
        const id = element?.dataset.reviewId ?? element?.dataset.blockId
        if (id && readBlockReviewTarget(root, api.state.blocks, id, review)) { selectReviewChange(root, id); return }
      }
      if (!next && root.dataset.reviewSelectedChange && event?.type !== 'pointerup') return
      if (next || event?.type === 'pointerup') clearReviewChangeHighlights(root)
      if (!sameReviewSelection(latest.current, next)) {
        if (!nativeOwner) nativeRange.current = null
        latest.current = next
        setSelection(next)
        setMenuOpen(false)
        review.onSelectionChange?.(next)
      }
    }
    const selectChange = (event: Event, replay = false) => {
      if (!replay) nativeRange.current = null
      const { blockId } = (event as CustomEvent<{ blockId: string | null }>).detail
      const next = blockId ? readBlockReviewTarget(root, api.state.blocks, blockId, review) : null
      if (!sameReviewSelection(latest.current, next)) {
        latest.current = next
        setSelection(next)
        setMenuOpen(false)
        review.onSelectionChange?.(next)
      }
    }
    const startBlockComment = (event: Event) => {
      const { blockId } = (event as CustomEvent<{ blockId: string }>).detail
      const target = readBlockReviewTarget(root, api.state.blocks, blockId, review)
      const element = root.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(blockId)}"], [data-review-id="${CSS.escape(blockId)}"]`)
      if (!target || !element || !review.onAddComment || draft) return
      const dom = root.ownerDocument.getSelection()
      const range = root.ownerDocument.createRange()
      range.selectNodeContents(element)
      dom?.removeAllRanges()
      dom?.addRange(range)
      latest.current = target
      setSelection(target)
      review.onSelectionChange?.(target)
      restore.current = api.mode === 'edit' ? highlightEditableDraft(root, range.cloneRange()) : markDraft(root, range.cloneRange())
      copyVersion.current += 1
      setCopyStatus('idle')
      setBody('')
      setDraft(true)
      if (mobileEnabled) openMobilePanel('selection', element)
    }
    root.addEventListener(START_BLOCK_COMMENT_EVENT, startBlockComment)
    root.addEventListener(SELECT_CHANGE_EVENT, selectChange)
    root.ownerDocument.addEventListener('selectionchange', update)
    root.addEventListener('pointerdown', pointerDown)
    root.addEventListener('pointerup', update)
    root.addEventListener('click', update)
    const selectedId = root.dataset.reviewSelectedChange
    if (selectedId) selectChange(new CustomEvent(SELECT_CHANGE_EVENT, { detail: { blockId: selectedId } }), true)
    else update()
    return () => {
      root.removeEventListener(START_BLOCK_COMMENT_EVENT, startBlockComment)
      root.removeEventListener(SELECT_CHANGE_EVENT, selectChange)
      root.ownerDocument.removeEventListener('selectionchange', update)
      root.removeEventListener('pointerdown', pointerDown)
      root.removeEventListener('pointerup', update)
      root.removeEventListener('click', update)
    }
  }, [api, review, draft, mobileEnabled, openMobilePanel])
  useLayoutEffect(() => {
    const root = api.root
    if (!root || !selection) return () => {}
    const place = () => {
      const node = toolbar.current
      if (!node) return
      if (draft) {
        const rootRect = root.getBoundingClientRect()
        const laneWidth = !mobileEnabled && window.innerWidth >= 1026 ? commentLaneWidth(window.innerWidth - rootRect.right) : null
        if (laneWidth) {
          const segment = selection.segments[0]
          const block = segment && blockElement(root, segment.blockId)
          let top = block?.getBoundingClientRect().top ?? selection.rect.top
          for (const card of root.parentElement?.querySelectorAll('[data-review-comment-card-position]') ?? []) {
            const rect = card.getBoundingClientRect()
            if (rect.top <= top && rect.bottom + 8 > top) top = rect.bottom + 8
          }
          node.style.removeProperty('position-area')
          node.style.removeProperty('position-try-fallbacks')
          Object.assign(node.style, { top: `${top}px`, left: `${rootRect.right + COMMENT_LANE_GAP}px`, width: `${laneWidth}px` })
        } else {
          for (const property of ['top', 'left', 'width']) node.style.removeProperty(property)
          if (api.mode === 'edit' && !mobileEnabled) {
            const position = reviewToolbarPosition(root, selection.rect, Math.min(node.offsetWidth || 288, window.innerWidth - 16), node.offsetHeight || 60, true)
            node.style.top = `${position.top}px`
            node.style.left = `${position.left}px`
            node.style.removeProperty('position-area')
            node.style.removeProperty('position-try-fallbacks')
          } else if (!mobileEnabled) {
            node.style.setProperty('position-area', 'block-end center')
            node.style.setProperty('position-try-fallbacks', 'flip-block')
          }
        }
        return
      }
      const bounds = toolbar.current?.getBoundingClientRect()
      const mark = root.querySelector<HTMLElement>('[data-review-comment-pending]')
      const targetId = selection.segments[0]?.diffId ?? (selection.scope === 'block'
        ? root.dataset.reviewSelectedChange ?? selection.segments[0]?.blockId : undefined)
      const blockRect = selection.scope === 'block' && targetId ? blockElement(root, targetId)?.getBoundingClientRect() : null
      const rect = mark?.getBoundingClientRect() ?? blockRect ?? (targetId ? reviewChangeRect(root, targetId) : null) ?? getOwnedReviewSelectionRect(root, touch) ?? selection.rect
      const width = bounds?.width ?? 288
      const height = toolbar.current?.offsetHeight ?? bounds?.height ?? 40
      const position = reviewToolbarPosition(root, rect, Math.min(width, window.innerWidth - 16), height, touch)
      if (toolbar.current) Object.assign(toolbar.current.style, { top: `${position.top}px`, left: `${position.left}px` })
    }
    place()
    const observer = new ResizeObserver(place)
    observer.observe(root)
    if (root.parentElement) observer.observe(root.parentElement)
    if (toolbar.current) observer.observe(toolbar.current)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => { observer.disconnect(); window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true) }
  })
  if (!review || api.mode === 'answer' || !selection || !api.root) return null
  if (!draft && actions.length === 0 && !showCommentAction) return null
  function beginComment() {
    const dom = api.root?.ownerDocument.getSelection()
    if (!api.root || !dom || !selection) return
    if (!dom.rangeCount || dom.isCollapsed || !api.root.contains(dom.anchorNode) || !api.root.contains(dom.focusNode)) {
      const retained = nativeRange.current
      if (retained && !retained.collapsed && api.root.contains(retained.startContainer) && api.root.contains(retained.endContainer)) {
        dom.removeAllRanges()
        dom.addRange(retained.cloneRange())
      } else {
        if (selection.segments.length !== 1 || selection.scope !== 'block' && !api.root.dataset.reviewSelectedChange) return
        const segment = selection.segments[0]
        const element = segment && blockElement(api.root, segment.diffId ?? segment.blockId)
        if (!element) return
        const range = api.root.ownerDocument.createRange()
        range.selectNodeContents(element)
        dom.removeAllRanges()
        dom.addRange(range)
      }
    }
    if (dom.isCollapsed && selection.scope !== 'block') return
    copyVersion.current += 1
    setCopyStatus('idle')
    restore.current = api.mode === 'edit' ? highlightEditableDraft(api.root, dom.getRangeAt(0).cloneRange()) : markDraft(api.root, dom.getRangeAt(0).cloneRange())
    setBody('')
    setDraft(true)
    if (mobileEnabled) openMobilePanel('selection', api.root)
  }
  const composer = <form className={mobileEnabled ? 'flex w-full flex-col gap-2 [&>div]:rounded-none' : 'flex w-full max-w-[calc(100vw-32px)] flex-col gap-2'}
      onKeyDownCapture={event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); dismiss() }
        if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.requestSubmit() }
      }} onSubmit={event => {
        event.preventDefault()
        if (!body.trim()) return
        const submitted = body.trim()
        dismiss()
        review.onAddComment?.(selection, submitted)
      }}>
      <NestedCommentComposer focusOnMount compact bordered={false} placeholder="Comment…" onBlur={dismiss} onChange={setBody} testId="content-blocks-editor-review-comment-input" value={body}
        actions={body.trim() ? <IconButton data-testid="content-blocks-editor-review-comment-submit" type="submit" size="sm" variant="filled"><SendIcon />Add comment</IconButton> : null} />
      {review.onCopyBlockDiff && <Button type="button" size="sm" variant="text" className="self-start"
        data-testid="content-blocks-editor-review-copy-block-diff" disabled={copyStatus === 'pending'}
        onClick={() => {
          const version = ++copyVersion.current
          setCopyStatus('pending')
          void Promise.resolve().then(() => review.onCopyBlockDiff?.(selection)).then(
            () => { if (version === copyVersion.current) setCopyStatus('copied'); return null },
            () => { if (version === copyVersion.current) setCopyStatus('failed'); return null },
          )
        }}><CopyIcon />{copyStatus === 'copied' ? 'Diff copied' : copyStatus === 'failed' ? 'Copy failed · retry' : 'Copy block diff'}</Button>}
    </form>
  const content = <div ref={toolbar} role="toolbar" aria-label="Review actions"
    data-testid="content-blocks-editor-review-actions"
    className={`fixed z-40 max-w-[calc(100vw-16px)] flex flex-wrap items-center rounded-lg ${draft ? 'border-surface-3 bg-surface-3 w-72 gap-2 p-2' : 'gap-1'}`}
    style={draft && !mobileEnabled && api.mode === 'review' ? { positionAnchor: '--content-block-review-comment-draft', positionArea: 'block-end center', positionTryFallbacks: 'flip-block' } : undefined}
    onPointerDown={event => {
      if (!(event.target as HTMLElement).closest(`input, textarea, ${EDITABLE}`)) event.preventDefault()
    }}>
    {draft ? composer : <>
      {collapsedActions && <Button data-testid="content-blocks-editor-review-selection-menu" variant="text" onClick={() => { setMenuOpen(!menuOpen) }}>Selection actions</Button>}
      {(!collapsedActions || menuOpen) && <div role="group" aria-label="Review selection" className="flex items-center rounded-full">
        {actions.map(action => <IconButton key={action.id} aria-label={action.label} aria-keyshortcuts={action.shortcut} data-testid={action.label}
          loading={action.loading} semanticRole={action.semanticRole} variant="outlined"
          className="rounded-none border-0 first:rounded-l-full last:rounded-r-full"
          tooltipClassName={action.semanticRole === 'success' ? 'bg-surface-success text-on-surface-success [&_.fill-surface-3]:fill-surface-success' : action.semanticRole === 'error' ? 'bg-surface-error text-on-surface-error [&_.fill-surface-3]:fill-surface-error' : action.semanticRole === 'warning' ? 'bg-surface-warning text-on-surface-warning [&_.fill-surface-3]:fill-surface-warning' : undefined}
          onClick={() => { review.onAction?.(action.id, selection) }}>{action.icon ?? <SquareIcon />}{action.shortcut ? `${action.label} (${action.shortcut.toUpperCase()})` : action.label}</IconButton>)}
        {showCommentAction && <IconButton aria-label="Comment" aria-keyshortcuts="c" data-testid="Comment" semanticRole="warning" variant="outlined" className="rounded-none border-0 first:rounded-l-full last:rounded-r-full" tooltipClassName="bg-surface-warning text-on-surface-warning [&_.fill-surface-3]:fill-surface-warning" onClick={beginComment}><MessageSquareIcon />Comment (C)</IconButton>}
      </div>}
    </>}
  </div>
  if (mobileEnabled && mobile.active !== null && mobile.active !== 'selection') return null
  return draft && mobileEnabled
    ? <MobileReviewPanel id="selection" title="Comment" composer onClose={dismiss}>{composer}</MobileReviewPanel>
    : createPortal(content, api.root.ownerDocument.body)
}
